import React, { useCallback, useRef } from 'react';
import { Form, FloatingLabel } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrash, faXmark, faCloudArrowUp } from '@fortawesome/free-solid-svg-icons';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';

interface QuartoFormularioProps {
  formValues: {
    dsAnuncio: string;
    [key: string]: any;
  };
  handleFieldChange: (field: string, value: any) => void;
  validacaoAtiva: boolean;
  todasAsImagens: (File | string)[];
  numeroImagensSelecionadas: number;
  imagemPadrao: string;
  handleImageChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  handleRemoveImage: (index: number) => void;
  handleImageClick: (index: number) => void;
  setImagem?: (images: File[]) => void;
  setTodasAsImagens?: (images: (File | string)[]) => void;
  setNumeroImagensSelecionadas: (n: number) => void;
}

const QuartoFormulario: React.FC<QuartoFormularioProps> = ({
  formValues,
  handleFieldChange,
  validacaoAtiva,
  todasAsImagens,
  numeroImagensSelecionadas,
  imagemPadrao,
  handleImageChange,
  handleRemoveImage,
  handleImageClick,
  setTodasAsImagens,
  setImagem,
  setNumeroImagensSelecionadas
}) => {

  const inputFileRef = useRef<HTMLInputElement>(null);

  function reorder<T>(list: T[], startIndex: number, endIndex: number) {
    const result = Array.from(list);
    const [removed] = result.splice(startIndex, 1);
    result.splice(endIndex, 0, removed);
    return result;
  }

  function onDragEnd(result: any) {
    if (!result.destination) {
      return;
    }
    const items = reorder(todasAsImagens, result.source.index, result.destination.index);
    const apenasArquivos = items.filter(item => item instanceof File) as File[];

    if (setTodasAsImagens) {
      setTodasAsImagens(items);
    }
    if (setImagem) {
      setImagem(apenasArquivos);
    }
  }

  const handleDrop = useCallback((event: React.DragEvent) => {
    event.preventDefault();
    const files = Array.from(event.dataTransfer.files);
    const newImages = files.slice(0, 5 - todasAsImagens.length);

    if (newImages.length > 0) {
      const updatedImages = [...todasAsImagens, ...newImages];
      setTodasAsImagens?.(updatedImages);
      setImagem?.(updatedImages.filter(item => item instanceof File) as File[]);
      setNumeroImagensSelecionadas(updatedImages.length)
    }
  }, [todasAsImagens, setTodasAsImagens, setImagem, setNumeroImagensSelecionadas]);

  return (
    <>
      <div>
        <Form.Group className="mb-1 mt-3 d-flex align-items-center gap-2 flex-wrap containerEnviarImg">
          <div>
            <Form.Label
              className={`quantidadeImagens`}
              style={todasAsImagens.length === 5 ? { cursor: 'not-allowed' } : { cursor: 'pointer' }}
              column
              htmlFor="mdArquivo"
            >
              Escolha até 5 imagens
            </Form.Label>
          </div>
          <div>
            <Form.Text className={`text-muted contador ${numeroImagensSelecionadas === 5 ? 'contadorImagens' : ''}`}>
              {`${numeroImagensSelecionadas}/5 imagens selecionadas`}
            </Form.Text>
          </div>
          <Form.Control
            id="mdArquivo"
            ref={inputFileRef}
            name='mdArquivo'
            type="file"
            title="Escolha até 5 imagens"
            disabled={todasAsImagens.length >= 5}
            multiple
            accept="image/*"
            onChange={handleImageChange}
            style={{ display: 'none' }}
          />
        </Form.Group>

        <DragDropContext onDragEnd={onDragEnd}>
          <Droppable droppableId='images' type='list' direction='horizontal'>
            {(provided) => (
              <ul
                className='containerImagens mt-3'
                ref={provided.innerRef}
                {...provided.droppableProps}
                onDragOver={(e) => e.preventDefault()}
                onDrop={handleDrop}
                style={todasAsImagens.length > 0 ? { border: '1px dashed #b67c30' } : {}}
              >
                {todasAsImagens.length === 0 && (
                  <div onClick={() => inputFileRef.current?.click()} className='importarImagens'>
                    <FontAwesomeIcon icon={faCloudArrowUp} className='importarNuvem' color='#1c1e1f' />
                    <p>Importe suas imagens</p>
                    <p>Arraste ou clique para fazer o upload</p>
                  </div>
                )}
                {todasAsImagens.map((imagem, index) => {
                  const imagemInvalida = imagem instanceof File ? (!imagem.type.startsWith('image/') || imagem.type.startsWith('image/svg+xml')) : false;
                  const src = !imagemInvalida ? (imagem instanceof File ? URL.createObjectURL(imagem) : imagem) : undefined;
                  const textoAlternativo = imagemInvalida ? 'O Arquivo Selecionado é inválido' : `Imagem ${index}`;
                  const imagemPrimeiraPosicao = (index === 0) && imagem;
                  const capaDoAnuncio = <span className='capaImagem' onClick={() => !imagemInvalida && handleImageClick(index)}>Capa</span>;

                  const isDraggable = !!imagem && !imagemInvalida;
                  if (!imagem) return null;

                  return (
                    <Draggable draggableId={index.toString()} index={index} key={`image-${index}`} isDragDisabled={!isDraggable}>
                      {(providedDraggable) => (
                        <li
                          className={`imagemAnuncio ${imagem ? 'temImagem' : ''} ${imagemInvalida ? 'campo-invalid' : ''} ${imagemPrimeiraPosicao ? 'imagemPrimeiraPosicao' : ''}`}
                          ref={providedDraggable.innerRef}
                          {...(isDraggable ? providedDraggable.draggableProps : {})}
                          {...(isDraggable ? providedDraggable.dragHandleProps : {})}
                        >
                          {imagemPrimeiraPosicao && !imagemInvalida && capaDoAnuncio}
                          {imagem && (
                            <FontAwesomeIcon
                              icon={imagem instanceof File ? faXmark : faTrash}
                              className={`botaoImagem`}
                              onClick={(e) => { e.stopPropagation(); handleRemoveImage(index); }}
                            />
                          )}
                          <img
                            src={src}
                            alt={textoAlternativo}
                            onClick={() => !imagemInvalida && handleImageClick(index)}
                          />
                        </li>
                      )}
                    </Draggable>
                  );
                })}
                {provided.placeholder}
              </ul>
            )}
          </Droppable>
        </DragDropContext>

        {todasAsImagens.length > 0 &&
          <div className='avisoImagens d-flex mt-2 gap-1'>
            <span className='mensagemImgPrincipal'>
              Certifique-se de escolher a imagem principal do seu anúncio primeiro.
            </span>
          </div>
        }
      </div >
      <div>
        <FloatingLabel controlId="floatingTextarea2" label="Descrição" className='mt-2'>
          <Form.Control
            as="textarea"
            name='dsAnuncio'
            value={formValues.dsAnuncio}
            placeholder="Leave a comment here"
            style={{ height: '200px', resize: 'none' }}
            onChange={(event) => handleFieldChange(event.target.name, event.target.value)}
            className={`${validacaoAtiva && !formValues.dsAnuncio ? 'input-invalid' : ''}`}
          />
        </FloatingLabel>
      </div>
    </>
  );
};

export default QuartoFormulario;
